import { useRBACPermissions } from '@/contexts/useRBACPermissions'
import { Alert, Button, VStack } from '@chakra-ui/react'
import { useNavigate } from '@tanstack/react-router'
import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

type AuthorizedRouteProps = {
  children: ReactNode
  fallback?: ReactNode
}

/**
 * Component that wraps content and only shows it if user has admin rights
 * Shows an error message if user doesn't have permission to access the content
 */
export function AuthorizedRoute({ children, fallback }: AuthorizedRouteProps) {
  const { hasAdminRights } = useRBACPermissions()
  const { t } = useTranslation()
  const navigate = useNavigate()

  if (hasAdminRights()) {
    return <>{children}</>
  }

  if (fallback) {
    return <>{fallback}</>
  }

  // Default unauthorized message
  return (
    <VStack gap={6} align="center" justify="center" minH="400px" p={6}>
      <Alert.Root status="error" maxW="500px">
        <Alert.Indicator />
        <VStack align="start" gap={2}>
          <Alert.Title>{t('authorization.accessDenied')}</Alert.Title>
          <Alert.Description>
            {t('authorization.insufficientPermissions')}
          </Alert.Description>
        </VStack>
      </Alert.Root>
      <Button variant="outline" onClick={() => navigate({ to: '/' })}>
        {t('authorization.goToHome')}
      </Button>
    </VStack>
  )
}
